let cartRecsRoot = null;

/**
 * Initialize the cart recommendations grid
 */
function initCartRecommendationsGrid() {
  const container = document.getElementById("cart-recommendations");
  if (!container) {
    console.error("[CartRecs] #cart-recommendations not found in DOM. Check cart.liquid.");
    return;
  }

  // Clean up previous instance if it exists
  if (cartRecsRoot) {
    cartRecsRoot.unmount();
    container.innerHTML = "";
  }

  try {
    cartRecsRoot = ReactDOM.createRoot(container);
    cartRecsRoot.render(React.createElement(CartRecommendationsGrid));
  } catch (error) {
    console.error("[CartRecs] Error rendering grid:", error);
  }
}

// -------------------------------
// Main grid component
// -------------------------------
function CartRecommendationsGrid() {
  const [products, setProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [cartItemCount, setCartItemCount] = React.useState(0);

  const scrollContainerRef = React.useRef(null);
  const limit = 4; // show up to 4 products

  // On mount: fetch cart => load recs
  React.useEffect(() => {
    const load = async () => {
      const cartData = await fetchCartItemCount();
      await loadRecommendations(cartData);
    };
    load().catch((error) => console.error("[CartRecs] Error in useEffect load:", error));
  }, []);

  // Once products are loaded, initialize the theme's QuickAdd (if present)
  React.useEffect(() => {
    if (!loading && products.length > 0) {
      if (window.ProductQuickAdd && typeof window.ProductQuickAdd.init === "function") {
        window.ProductQuickAdd.init();
      }
    }
  }, [loading, products]);

  // Reset scroll position (in case it's used like a slider)
  React.useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [loading, products]);

  // --------------------------------
  // 1) Fetch cart data from /cart.js
  // --------------------------------
  async function fetchCartItemCount() {
    try {
      const cartRes = await fetch("/cart.js");
      if (cartRes.ok) {
        const data = await cartRes.json();
        const itemCount = (data.item_count !== undefined)
          ? data.item_count
          : (data.items?.length || 0);
        setCartItemCount(itemCount);
        return data;
      }
    } catch (err) {
      console.error("[CartRecs] fetchCartItemCount error:", err);
      setCartItemCount(0);
      return { items: [] };
    }
  }

  // --------------------------------
  // 2) Load recommendations
  // --------------------------------
  async function loadRecommendations(cartData) {
    setLoading(true);

    try {
      const cartItems = cartData.items || [];
      const cartProductIds = new Set(cartItems.map((i) => i.product_id));

      // If cart truly empty
      if (cartItems.length === 0) {
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // Cart has items => pick the first item (dom approach or direct from data)
      const firstItem = cartItems[0];
      if (!firstItem) {
        console.error("[CartRecs] No first item found, fallback to default");
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // We keep your original approach of reading from the DOM anchor if you want
      const productUrl = document.querySelector(".cart--item a")?.href;
      const productHandle = productUrl?.split("/products/")[1]?.split("?")[0];

      if (!productHandle) {
        console.error("[CartRecs] No product handle found, falling back to default");
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // Attempt up to 3 fetch tries
      let productData;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const productRes = await fetch(`/products/${productHandle}.js`);
          if (!productRes.ok) {
            console.error(`[CartRecs] Attempt ${attempt + 1} failed w/status ${productRes.status}`);
            if (attempt === 2) throw new Error("Max retries reached");
            await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
            continue;
          }
          productData = await productRes.json();
          break;
        } catch (err) {
          console.error("[CartRecs] Error fetching product data attempt", attempt + 1, ":", err);
        }
      }
      if (!productData || !productData.id) {
        console.error("[CartRecs] No product data or ID found, fallback to default");
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }

      // Now fetch Shopify's built-in recs
      const recUrl = `/recommendations/products.json?product_id=${productData.id}&limit=${limit}`;
      const recResp = await fetch(recUrl, { credentials: "same-origin" });
      if (!recResp.ok) {
        console.error("[CartRecs] recs fetch error:", recResp.status);
        await loadDefaultRecommendations(cartProductIds);
        setLoading(false);
        return;
      }
      const recData = await recResp.json();

      if (recData.products && recData.products.length > 0) {
        const recommendedProducts = await Promise.all(
          recData.products.map((p) => fetchFullProductData(p))
        );
        const uniqueProducts = recommendedProducts.filter(
          (p) => p && !cartProductIds.has(p.id)
        ).slice(0, limit);
        setProducts(uniqueProducts);
      } else {
        // fallback
        await loadDefaultRecommendations(cartProductIds);
      }
    } catch (err) {
      console.error("[CartRecs] Error loading recs:", err);
      setProducts([]);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  // --------------------------------
  // 3) Load default recs if empty/fallback
  // --------------------------------
  async function loadDefaultRecommendations(cartProductIds) {
    const recUrl = `/recommendations/products.json?product_id=6763403411517&limit=${limit}`;
    try {
      const recResp = await fetch(recUrl, { credentials: "same-origin" });
      if (!recResp.ok) {
        console.error("[CartRecs] default recs fetch error:", recResp.status);
        setProducts([]);
        return;
      }
      const recData = await recResp.json();

      if (recData.products && recData.products.length > 0) {
        const recommendedProducts = await Promise.all(
          recData.products.map((p) => fetchFullProductData(p))
        );
        const uniqueProducts = recommendedProducts.filter(
          (p) => p && !cartProductIds.has(p.id)
        ).slice(0, limit);
        setProducts(uniqueProducts);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error("[CartRecs] Error fetching default recs:", err);
      setProducts([]);
    }
  }

  // --------------------------------
  // 4) Fetch full product data
  // --------------------------------
  async function fetchFullProductData(product) {
    try {
      const fullRes = await fetch(`/products/${product.handle}.js`);
      if (!fullRes.ok) throw new Error("Failed to fetch full product data");
      const fullData = await fullRes.json();

      return {
        id: product.id,
        handle: product.handle,
        url: `/products/${product.handle}`,
        title: product.title,
        price: formatPrice(fullData.price),
        compareAtPrice: fullData.compare_at_price
          ? formatPrice(fullData.compare_at_price)
          : null,
        image:
          fullData.featured_image ||
          (fullData.images && fullData.images[0]) ||
          "/api/placeholder/300/300",
        secondaryImage:
          fullData.images && fullData.images[1] ? fullData.images[1] : null,
        available: fullData.available,
        hasOnlyDefaultVariant: fullData.variants && fullData.variants.length === 1,
        variants: fullData.variants || [],
      };
    } catch (err) {
      console.error("[CartRecs] Error fetching full product data:", err);
      setLoading(false);
      return null;
    }
  }

  // --------------------------------
  // 5) The theme's quick add usage
  //    We rely on .product--quick-add and
  //    data-handle / data-id for the theme.
  //    No custom add code here.
  // --------------------------------
  function renderQuickAdd(product) {
    if (!product.available) return null;

    const buttonText = product.hasOnlyDefaultVariant ? "Quick Add" : "Choose Options";
    const props = {
      className: "product--quick-add",
      "aria-busy": isUpdating ? "true" : "false",
      "data-handle": product.handle,  // The theme sees this for quick-add
      "data-form": product.hasOnlyDefaultVariant ? "false" : "true",
      style: { position: "absolute", bottom: "0", right: "0", padding: "var(--spacing--1)" },
    };

    // If single variant, set data-id so the theme can auto-add
    if (product.hasOnlyDefaultVariant && product.variants?.length) {
      props["data-id"] = product.variants[0].id;
      // Note: No custom onClick because we let the theme do it
    }

    return React.createElement(
      "product-quick-add",
      props,
      React.createElement(
        "button",
        {
          className: "product-quick-add--button",
          type: "button",
          "aria-haspopup": "menu",
          "aria-label": buttonText,
          style: { zIndex: 21, position: "relative" },
        },
        // Icon
        React.createElement(
          "div",
          { className: "product-quick-add--icon" },
          React.createElement(
            "svg",
            {
              className: "icon--root icon--plus",
              width: "10",
              height: "10",
              viewBox: "0 0 10 10",
              xmlns: "http://www.w3.org/2000/svg",
            },
            React.createElement("path", {
              d: "M10 5.714H5.714V10H4.286V5.714H0V4.286h4.286V0h1.428v4.286H10z",
              fill: "#000",
              "fill-rule": "nonzero",
            })
          )
        ),
        // Button text
        React.createElement(
          "div",
          { className: "product-quick-add--text", "data-item": "small-text" },
          buttonText
        ),
        // Spinner if needed
        React.createElement(
          "div",
          {
            className: "product--spinner",
            style: { display: isUpdating ? "block" : "none" },
          }
        )
      )
    );
  }

  // --------------------------------
  // 6) Format price helper
  // --------------------------------
  function formatPrice(cents) {
    if (!cents || isNaN(cents)) return "$0.00";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: Shopify.currency.active,
    }).format(cents / 100);
  }

  // --------------------------------
  // 7) Render the grid
  // --------------------------------
  return React.createElement(
    "div",
    {
      className: "cart--recommendations",
      style: {
        borderTop: "1px solid var(--color--border)",
        marginTop: "32px",
        paddingTop: "32px",
      },
    },
    React.createElement(
      "h3",
      {
        "data-item": "section-heading",
        style: { fontSize: "18px", marginBottom: "24px", paddingLeft: "16px" },
      },
      cartItemCount > 0 ? "Just For You" : "Discover More"
    ),
    React.createElement(
      "div",
      {
        ref: scrollContainerRef,
        className: "cart--recommendations--items",
        style: {
          display: "flex",
          overflowX: "auto",
          gap: "16px",
          padding: "0 48px 16px",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          minHeight: "200px",
        },
      },
      loading
        ? React.createElement("div", { style: { padding: "16px", textAlign: "center" } }, "")
        : products.length > 0
        ? // Within the CartRecommendationsGrid component's return statement
products.map((product) =>
  React.createElement(
    "div",
    {
      key: product.id,
      className: "product-card--root",
      style: {
        flex: "0 0 auto",
        width: "200px",
        scrollSnapAlign: "start",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      },
    },
    React.createElement(
      "a",
      {
        href: product.url,
        className: "product-card--image-wrapper",
        style: {
          display: "block",
          position: "relative",
          paddingBottom: "100%",
          overflow: "hidden",
        },
      },
      React.createElement("img", {
        src: product.image,
        alt: product.title,
        className: "product-card--image",
        style: {
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        },
        onError: (e) => {
          e.target.src = "/api/placeholder/300/300";
        },
      }),
      product.secondaryImage &&
        React.createElement("img", {
          src: product.secondaryImage,
          alt: product.title + " - Second View",
          className: "product-card--hover-image",
          style: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          },
          onError: (e) => {
            e.target.style.display = "none";
          },
        }),
      renderQuickAdd(product)
    ),
    // Updated Product Details Section
    React.createElement(
      "div",
      {
        className: "product-card--details",
        style: {
          padding: "8px 0",
          textAlign: "left",
          width: "100%", // Ensure full width to align with card
        },
      },
      React.createElement(
        "h4",
        {
          className: "product--title",
          style: {
            marginBottom: "8px",
            fontSize: "14px",
            lineHeight: "1.2",
            fontWeight: "normal",
            textTransform: "uppercase", // Make headings uppercase
            display: "block", // Force vertical stacking
          },
        },
        product.title
      ),
      React.createElement(
        "div",
        {
          className: "product--price",
          style: {
            fontSize: "14px",
            display: "block", // Force vertical stacking
          },
        },
        product.compareAtPrice &&
          React.createElement(
            "s",
            {
              style: {
                color: "var(--color--body--light)",
                marginRight: "8px",
              },
            },
            product.compareAtPrice
          ),
        React.createElement("span", { className: "money" }, product.price)
      )
    )
  )
)
        : React.createElement("div", { style: { padding: "16px", textAlign: "center" } }, "No products available.")
    )
  );
}

// -------------------------------
// On DOM load, init once
// -------------------------------
document.addEventListener("DOMContentLoaded", () => {
  initCartRecommendationsGrid();
});

// If theme triggers "theme:cart:updated", we re-init
let debounceTimer;
window.addEventListener("theme:cart:updated", () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    initCartRecommendationsGrid();

    // If the theme's QuickAdd script needs re-init
    if (window.ProductQuickAdd && typeof window.ProductQuickAdd.init === "function") {
      window.ProductQuickAdd.init();
    }
  }, 500);
});